// Bulletins types for React Native admin app
// Based on admin-web V2 bulletins implementation

// React Native file object type
export interface ReactNativeFile {
  uri: string;
  type: string;
  name: string;
  size?: number;
}

// Bulletin author type
export interface BulletinAuthor {
  id: number;
  firstName?: string;
  lastName?: string;
  email: string;
}

export interface IBulletinPhoto {
  id?: number; // Make id optional to match GenericPhoto
  file?: File | ReactNativeFile;
  photoUrl?: string;
  fileUrl?: string;
}

export interface ICreateBulletin {
  title: string;
  message: string; // Plain text or HTML string (converted from rich text)
  propertyId: number;
  publishType?: 'publishNow' | 'draft' | 'scheduled';
  publishDate?: string | null; // ISO date string
  highlight: boolean;
  sendEmail: boolean;
  photos?: IBulletinPhoto[];
  pinHomepage: boolean;
  recipientType: 'all' | 'current' | 'future' | 'notice';
}

export interface IBulletin extends ICreateBulletin {
  id: number;
  subject?: string; // V1 compatibility
  description?: string; // V1 compatibility  
  descriptionJson?: string; // V1 compatibility
  descriptionHtml?: string; // V1 compatibility
  createdAt: string | null;
  updatedAt: string | null;
  messageSentCount: number;
  messageReadCount: number;
  pushNotificationSentCount: number;
  files: IBulletinPhoto[];
  author?: BulletinAuthor;
}

export interface IBulletinListResponse {
  data: IBulletin[];
  count: number;
  hasMore?: boolean;
}

// Table row interface for display
export interface IBulletinTableRow {
  id: number;
  title: string;
  subject: string; // For display compatibility
  date: Date | null;
  time: Date | null;
  // Scheduled publish timestamp (ISO string) for displaying "Scheduled at …"
  publishDate?: string | null;
  messageSentCount: number;
  messageReadCount: number;
  pushNotificationSentCount: number;
  highlight: boolean;
  pinHomepage: boolean;
  publishType?: string;
  recipientType: string;
  updatedAt?: string | null; // Timestamp of last update for unique keys
  // Additional fields for v2 compatibility
  description?: string;
  author?: BulletinAuthor | null;
  files?: IBulletinPhoto[];
}

// Filter model for bulletins (matching admin-web v2)
export interface IBulletinFilterModel {
  showScheduled: boolean;
  showDraft: boolean;
  recipientType: 'all' | 'current' | 'future' | 'notice';
}

// Sort model for bulletins
export interface IBulletinSortModel {
  field: 'title' | 'createdAt' | 'messageSentCount' | 'messageReadCount';
  sort: 'asc' | 'desc';
}

// Note: IPaginationModel is imported from dataTable types

// Form values for creating/editing bulletins
export interface IBulletinFormValues {
  title: string;
  message: string;
  publishType: 'publishNow' | 'draft' | 'scheduled';
  publishDate?: string | null;
  publishTime?: Date | null; // Date object for date picker
  highlight: boolean;
  sendEmail: boolean;
  pinHomepage: boolean;
  recipientType: 'all' | 'current' | 'future' | 'notice';
  photos?: IBulletinPhoto[];
}

// Default form values
export const defaultBulletinFormValues: IBulletinFormValues = {
  title: '',
  message: '',
  publishType: 'publishNow',
  publishDate: null,
  publishTime: null,
  highlight: false,
  sendEmail: false,
  pinHomepage: false,
  recipientType: 'all',
  photos: [],
};

// Feed filter options
export const BULLETIN_FEED_FILTERS = {
  FEED: 'Feed',
  SCHEDULED: 'Scheduled', 
  DRAFT: 'Draft',
} as const;

export type BulletinFeedFilter = typeof BULLETIN_FEED_FILTERS[keyof typeof BULLETIN_FEED_FILTERS];

// Recipient type options
export const BULLETIN_RECIPIENT_TYPES = {
  ALL: 'all',
  NOTICE: 'notice',
} as const;

export type BulletinRecipientType = 'all' | 'current' | 'future' | 'notice';

// Table column keys for bulletins
export enum BulletinTableKeys {
  TITLE = 'title',
  DATE = 'date',
  TIME = 'time',
  MESSAGE_SENT_COUNT = 'messageSentCount',
  MESSAGE_READ_COUNT = 'messageReadCount',
  PUSH_NOTIFICATION_SENT_COUNT = 'pushNotificationSentCount',
}

// API Response Types
export interface BulletinApiResponse {
  success: boolean;
  data?: IBulletin;
  message?: string;
  error?: string;
  warning?: string | null;
  bulletinId?: number;
}

export interface BulletinListApiResponse {
  success: boolean;
  data: IBulletin[];
  count: number;
  hasMore?: boolean;
}

export interface BulletinUnreadCountResponse {
  success: boolean;
  count: number;
}

// Form Field Handler Types
export type BulletinFormFieldValue<K extends keyof IBulletinFormValues> = IBulletinFormValues[K];

export type BulletinFormFieldHandler = <K extends keyof IBulletinFormValues>(
  field: K,
  value: BulletinFormFieldValue<K>
) => void;

// Error types for form validation
export interface BulletinFormErrors {
  title?: string;
  message?: string;
  publishDate?: string;
  publishTime?: string;
  photos?: string;
  [key: string]: string | undefined;
}
